import { useState, useEffect } from 'react'
import { supabase } from '../../lib/supabase'
import {
  fetchParentStudents,
  getActiveStudentId
} from '../../lib/parentStudents'

export default function ParentReports() {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    async function fetchData() {
      const { data: { user } } = await supabase.auth.getUser()

      if (!user) {
        setLoading(false)
        return
      }

      const { students } =
        await fetchParentStudents(user.id)

      const activeId =
        getActiveStudentId(students)

      const student =
        students.find(s => s.id === activeId)

      if (!student) {
        setLoading(false)
        return
      }

      const sid = student.id

      const [
        { data: grades },
        { data: att },
        { data: hw }
      ] = await Promise.all([
        supabase
          .from('grades')
          .select('*')
          .eq('student_id', sid),

        supabase
          .from('attendance')
          .select('*')
          .eq('student_id', sid),

        supabase
          .from('homework')
          .select('*')
          .eq('student_id', sid),
      ])

      const avgGrade = grades?.length
        ? Math.round(
            grades.reduce(
              (a, g) =>
                a + (g.score / g.total * 100),
              0
            ) / grades.length
          )
        : null

      const attPct = att?.length
        ? Math.round(
            att.filter(
              a => a.status === 'present'
            ).length /
            att.length * 100
          )
        : null

      const hwPct = hw?.length
        ? Math.round(
            hw.filter(
              h => h.status === 'done'
            ).length /
            hw.length * 100
          )
        : null

      const overall = Math.round(
        ((avgGrade ?? 50) * .5) +
        ((attPct ?? 50) * .3) +
        ((hwPct ?? 50) * .2)
      )

      const gradeLabel =
        overall >= 90
          ? 'Excellent'
          : overall >= 75
            ? 'Very Good'
            : overall >= 60
              ? 'Good'
              : overall >= 50
                ? 'Pass'
                : 'Needs Improvement'

      const gradeColor =
        overall >= 90
          ? 'var(--success)'
          : overall >= 75
            ? '#185fa5'
            : overall >= 60
              ? '#534ab7'
              : overall >= 50
                ? 'var(--warning)'
                : 'var(--danger)'

      setData({
        student,
        avgGrade,
        attPct,
        hwPct,
        overall,
        gradeLabel,
        gradeColor,
        totalExams: grades?.length,
        grades: grades?.sort(
          (a, b) =>
            new Date(b.created_at) -
            new Date(a.created_at)
        )
      })

      setLoading(false)
    }

    fetchData()

    window.addEventListener(
      'parent-student-changed',
      fetchData
    )

    return () => {
      window.removeEventListener(
        'parent-student-changed',
        fetchData
      )
    }
  }, [])

  const pctClass = p =>
    p == null
      ? ''
      : p >= 80
        ? 'badge-success'
        : p >= 60
          ? 'badge-warning'
          : 'badge-danger'

  if (loading) {
    return <div className="empty">Loading...</div>
  }

  if (!data) {
    return (
      <div className="empty">
        No data available
      </div>
    )
  }

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      gap: 16
    }}>

      <div className="page-header">
        <h2>Report Card</h2>
      </div>

      <div
        className="card"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 14
        }}
      >
        <div style={{
          width: 48,
          height: 48,
          borderRadius: '50%',
          background: 'var(--accent-bg)',
          color: 'var(--accent)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 20,
          fontWeight: 700
        }}>
          {data.student?.name
            ?.charAt(0)
            ?.toUpperCase()}
        </div>

        <div style={{ minWidth: 0 }}>
          <div style={{
            fontWeight: 600,
            fontSize: 16
          }}>
            {data.student?.name}
          </div>

          {data.student?.grade && (
            <div style={{
              fontSize: 13,
              color: 'var(--text3)',
              marginTop: 2
            }}>
              📚 {data.student.grade}
            </div>
          )}

          <div style={{
            fontSize: 12,
            color: 'var(--text3)',
            marginTop: 2
          }}>
            {data.student?.student_code}
          </div>
        </div>
      </div>

      <div
        className="card"
        style={{
          textAlign: 'center',
          padding: 28
        }}
      >
        <div style={{
          fontSize: 13,
          color: 'var(--text3)',
          marginBottom: 8
        }}>
          Overall Performance
        </div>

        <div style={{
          fontSize: 52,
          fontWeight: 700,
          color: data.gradeColor,
          lineHeight: 1
        }}>
          {data.overall}%
        </div>

        <div style={{
          fontSize: 17,
          fontWeight: 500,
          color: data.gradeColor,
          marginTop: 8
        }}>
          {data.gradeLabel}
        </div>
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns:
          'repeat(3,1fr)',
        gap: 10
      }}>
        {[
          {
            label: 'Exams',
            val: data.avgGrade != null
              ? data.avgGrade + '%'
              : '—',
            p: data.avgGrade
          },
          {
            label: 'Attendance',
            val: data.attPct != null
              ? data.attPct + '%'
              : '—',
            p: data.attPct
          },
          {
            label: 'Homework',
            val: data.hwPct != null
              ? data.hwPct + '%'
              : '—',
            p: data.hwPct
          },
        ].map(s => (
          <div
            key={s.label}
            className="card"
            style={{
              textAlign: 'center',
              padding: 14
            }}
          >
            <div style={{
              fontSize: 11,
              color: 'var(--text3)',
              marginBottom: 6,
              textTransform: 'uppercase',
              letterSpacing: .04
            }}>
              {s.label}
            </div>

            <span
              className={`badge ${pctClass(s.p)}`}
              style={{ fontSize: 14 }}
            >
              {s.val}
            </span>
          </div>
        ))}
      </div>

      {data.grades?.length > 0 && (
        <>
          <div className="desktop-table">
            <div
              className="card"
              style={{
                padding: 0,
                overflow: 'hidden'
              }}
            >
              <table>
                <thead>
                  <tr>
                    <th>Exam</th>
                    <th>Date</th>
                    <th>Score</th>
                    <th>%</th>
                  </tr>
                </thead>

                <tbody>
                  {data.grades.map(g => {
                    const p =
                      Math.round(
                        g.score / g.total * 100
                      )

                    return (
                      <tr key={g.id}>
                        <td style={{
                          fontWeight: 500
                        }}>
                          {g.exam_name}
                        </td>

                        <td style={{
                          color: 'var(--text3)'
                        }}>
                          {g.exam_date || '—'}
                        </td>

                        <td>
                          {g.score}/{g.total}
                        </td>

                        <td>
                          <span className={`badge ${pctClass(p)}`}>
                            {p}%
                          </span>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </div>

          <div className="mobile-cards">
            {data.grades.map(g => {
              const p =
                Math.round(
                  g.score / g.total * 100
                )

              return (
                <div
                  key={g.id}
                  className="mobile-card"
                  style={{
                    display: 'flex',
                    justifyContent:
                      'space-between',
                    alignItems: 'center',
                    gap: 12
                  }}
                >
                  <div style={{ minWidth: 0 }}>
                    <div style={{
                      fontWeight: 600,
                      overflowWrap:
                        'anywhere'
                    }}>
                      {g.exam_name}
                    </div>

                    <div style={{
                      fontSize: 12,
                      color: 'var(--text3)',
                      marginTop: 2
                    }}>
                      {g.exam_date || '—'}
                    </div>
                  </div>

                  <div style={{
                    textAlign: 'right',
                    flexShrink: 0
                  }}>
                    <div style={{
                      fontWeight: 500
                    }}>
                      {g.score}/{g.total}
                    </div>

                    <span
                      className={`badge ${pctClass(p)}`}
                      style={{ marginTop: 4 }}
                    >
                      {p}%
                    </span>
                  </div>
                </div>
              )
            })}
          </div>
        </>
      )}
    </div>
  )
}